/**
 * 객체 타입별 기본 ID Prefix 정의
 */

/**
 * 주차면 타입 Prefix
 */
export const PARKING_PREFIXES: Record<string, string> = {
  'p-parking-basic': 'parkingLocations',        // 일반 주차
  'p-parking-disabled': 'parkingLocations',     // 장애인 주차
  'p-parking-electric': 'parkingLocations',     // 전기차 주차
  'p-parking-women': 'parkingLocations',        // 여성 주차
  'p-parking-compact': 'parkingLocations',      // 경차 주차
  'p-parking-delivery': 'parkingLocations',     // 택배 주차
  'p-parking-large': 'parkingLocations',        // 대형차 주차
  'p-parking-premium': 'parkingLocations',      // 프리미엄 주차
};

/**
 * 시설물 타입 Prefix
 *
 * 매뉴얼 placement (useObjectCreation) 에서 한글 displayName 대신 사용하는
 * canonical layer key 도 함께 등재 — 이 키들이 generateObjectName 의
 * `${prefix}-N` 패턴에 그대로 들어가서 lightCctv-1, warningCctv-1,
 * carCharger-1 같은 이름이 자동 생성된다.
 */
export const FACILITY_PREFIXES: Record<string, string> = {
  'p-cctv': 'cctvs',              // CCTV
  'p-lightCctv': 'lightCctv',     // 조명 CCTV (placement 전용)
  'p-warningCctv': 'warningCctv', // 단방향 CCTV (placement 전용)
  'c-carCharger': 'carCharger',   // 전기 충전기 (placement 전용)
  // DXF 표준 kebab-case (layer-mapping 흐름)
  'p-light-cctv': 'lightCctv',
  'p-warning-cctv': 'warningCctv',
  'p-sensor': 'sensors',          // 센서
  'p-emergency': 'emergencyBells', // 비상벨
  'p-speaker': 'speakers',        // 스피커
  'p-light': 'lightLines',         // 조명
  'l-lightingline': 'lightLines',  // 조명 라인
  'p-fire-extinguisher': 'fireExtinguishers', // 소화기
  'p-pillar': 'columns',          // 기둥
  'p-wall': 'walls',              // 벽
  's-outline': 'outlines',        // 외곽선 (건물/벽체 polyline)
};

/**
 * 동선 타입 Prefix
 */
export const CIRCULATION_PREFIXES: Record<string, string> = {
  's-circulation-stairs': 'CS',   // Circulation Stairs (계단)
  's-circulation-exit': 'CE',     // Circulation Exit (비상구)
  's-circulation-entrance': 'CN',  // Circulation eNtrance (출입구)
  's-circulation-ramp': 'CR',     // Circulation Ramp (램프)
  's-circulation-elevator': 'CV',  // Circulation eleVator (엘리베이터)
  's-circulation-path': 'CP',     // Circulation Path (통로)
};

/**
 * 기타 타입 Prefix
 */
export const OTHER_PREFIXES: Record<string, string> = {
  'unknown': 'UN',                // UNknown (알 수 없음)
  'custom': 'CU',                 // CUstom (사용자 정의)
  'zone': 'ZN',                   // ZoNe (구역)
  'area': 'AR',                   // ARea (영역)
};

/**
 * 모든 기본 Prefix 통합
 */
export const DEFAULT_PREFIXES: Record<string, string> = {
  ...PARKING_PREFIXES,
  ...FACILITY_PREFIXES,
  ...CIRCULATION_PREFIXES,
  ...OTHER_PREFIXES,
};

/**
 * 층 정보 매핑
 */
export const FLOOR_MAPPINGS: Record<string, string> = {
  'B5': 'B5',   // 지하 5층
  'B4': 'B4',   // 지하 4층
  'B3': 'B3',   // 지하 3층
  'B2': 'B2',   // 지하 2층
  'B1': 'B1',   // 지하 1층
  '1F': '01',   // 1층
  '2F': '02',   // 2층
  '3F': '03',   // 3층
  '4F': '04',   // 4층
  '5F': '05',   // 5층
  '6F': '06',   // 6층
  '7F': '07',   // 7층
  '8F': '08',   // 8층
  '9F': '09',   // 9층
  '10F': '10',  // 10층
  'RF': 'RF',   // Roof (옥상)
  'PH': 'PH',   // Penthouse
};

/**
 * Prefix 그룹 정보 (UI 표시용)
 */
export const PREFIX_GROUPS = [
  {
    name: '주차면',
    key: 'parking',
    prefixes: PARKING_PREFIXES,
  },
  {
    name: '시설물',
    key: 'facility',
    prefixes: FACILITY_PREFIXES,
  },
  {
    name: '동선',
    key: 'circulation',
    prefixes: CIRCULATION_PREFIXES,
  },
  {
    name: '기타',
    key: 'other',
    prefixes: OTHER_PREFIXES,
  },
];

/**
 * Legacy ID 패턴 (마이그레이션용)
 */
export const LEGACY_ID_PATTERNS = [
  /^entity_\d+_[\d.]+$/,        // entity_1710123456_0.123
  /^polygon_[a-z0-9]+$/,         // polygon_abc123
  /^LWPOLYLINE_\d+$/,           // LWPOLYLINE_456
  /^[0-9A-F]+$/,                // 16진수 (DXF EntityHandle)
];

/**
 * Legacy ID 감지
 */
export function isLegacyId(id: string): boolean {
  return LEGACY_ID_PATTERNS.some(pattern => pattern.test(id));
}

/**
 * 타입에서 기본 Prefix 가져오기
 */
export function getDefaultPrefix(type: string): string {
  return DEFAULT_PREFIXES[type] || 'XX';
}

/**
 * 층 정보 정규화
 */
export function normalizeFloor(floor: string): string {
  return FLOOR_MAPPINGS[floor?.toUpperCase()] || floor || '';
}